import React from "react";
import { Link } from "react-router-dom";
import { resolveImgUrl, imgSrcSet } from "@/components/ImageUploader";
import { ProductPrice } from "@/components/ProductPrice";
import { useWishlist } from "@/context/WishlistContext";
import { StarRating } from "@/components/Reviews";
import { Heart } from "lucide-react";

export default function ProductCard({ product, onAdd, preferredColor, showTrending = true }) {
  const { has, toggle } = useWishlist();
  const isWishlisted = has(product.product_id);
  const tone = product.category === "girls" ? "pink" : "sky";
  const matchedVariant = preferredColor
    ? product.colors?.find((c) => c.name?.toLowerCase() === preferredColor.toLowerCase())
    : null;
  const displayImage = matchedVariant?.images?.[0] || product.images?.[0];

  return (
    <div className="product-card group relative bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden" data-testid={`product-card-${product.product_id}`}>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(product); }}
        data-testid={`wishlist-toggle-${product.product_id}`}
        className={`absolute top-3 right-3 z-10 w-9 h-9 rounded-full flex items-center justify-center transition-all ${isWishlisted ? "bg-pink-500 text-white" : "bg-white/90 text-slate-700 hover:bg-white"} shadow-md`}
        aria-label={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
      >
        <Heart className={`w-4 h-4 ${isWishlisted ? "fill-current" : ""}`}/>
      </button>
      <Link to={matchedVariant ? `/product/${product.product_id}?color=${encodeURIComponent(matchedVariant.name)}` : `/product/${product.product_id}`} className="block">
        <div className="relative aspect-[3/4] overflow-hidden bg-slate-100">
          <img
            src={resolveImgUrl(displayImage, 600)}
            srcSet={imgSrcSet(displayImage, [200, 300, 400, 600])}
            sizes="(max-width: 640px) 50vw, 300px"
            alt={`${product.name}${product.item_code ? ` - ${product.item_code}` : ""}`}
            className="product-card-img absolute inset-0 w-full h-full object-contain"
            loading="lazy"
          />
          {product.featured && (
            <span className="absolute bottom-3 left-3 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-yellow-300 text-slate-900">Featured</span>
          )}
        </div>
        <div className="p-4">
          <div className={`text-[10px] uppercase tracking-[0.15em] font-bold mb-1 ${tone === "pink" ? "text-pink-500" : "text-sky-500"}`}>
            {product.category === "girls" ? "Chotti Girls" : "Chotta Boyz"}
          </div>
          <h3 className="font-display font-medium text-slate-900 line-clamp-2 min-h-[2.5rem]">{product.name}</h3>
          {product.review_count > 0 && (
            <div className="flex items-center gap-1.5 mt-1" data-testid={`product-rating-${product.product_id}`}>
              <StarRating value={product.avg_rating} size={14} />
              <span className="text-xs text-slate-400">({product.review_count})</span>
            </div>
          )}
          {showTrending && product.trending_events > 0 && (
            <div className="text-[10px] uppercase tracking-wider font-semibold text-orange-500 mt-1" data-testid={`trending-pill-${product.product_id}`}>
              🔥 {product.trending_events} this week
            </div>
          )}
          <ProductPrice price={product.price} mrp={product.mrp} size="md" showDiscount className="mt-2" />
        </div>
      </Link>
      <button
        onClick={(e) => { e.preventDefault(); onAdd?.(product); }}
        data-testid={`quick-add-${product.product_id}`}
        className={`absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 ${tone === "pink" ? "bg-pink-500 hover:bg-pink-600" : "bg-sky-500 hover:bg-sky-600"} text-white text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-full shadow-lg btn-bouncy`}
      >
        + Add
      </button>
    </div>
  );
}
